<template>
  <BaseModal
    card-width="488px"
    no-padding
    no-toolbar-padding
    no-exit
    @close="$emit('close')"
  >
    <template #toolbar>
      <div
        v-if="!createdNewReport"
        class="w-full flex items-start gap-3"
      >
        <StaticCopyIcon class="text-icon-muted" />
        <div>
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Duplicate Report
          </BaseText>
          <BaseText
            size="sm"
            class="text-text-normal"
          >
            Duplicate this report to the same or different Lens account.
          </BaseText>
        </div>
      </div>
      <div
        v-else
        class="flex items-center gap-2"
      >
        <SuccessPartyIcon />
        <BaseText
          class="text-text-muted"
          size="sm"
          type="label"
        >
          Done! Your report is ready.
        </BaseText>
      </div>
      <button
        class="absolute top-3 right-3 p-1.5"
        @click="$emit('close')"
      >
        <TagRemoveIcon stroke="#5E6678" />
      </button>
    </template>
    <template #default>
      <BaseHeightTransition>
        <!-- MODAL SECTION - Duplicate report details -->
        <div
          v-if="!createdNewReport"
          key="createReport"
          class="w-full flex flex-col items-stretch"
        >
          <!-- Modal body -->
          <div class="w-full h-full p-5 border-t border-b mt-4 flex flex-col gap-4">
            <!-- Ad Account -->
            <div class="w-full space-y-1">
              <div v-if="loading.lensAccounts">
                <div class="w-full skeleton h-5 rounded-lg" />
                <div class="w-full skeleton h-8 rounded-lg" />
              </div>
              <!-- <BaseSingleDropdown
                v-else
                label="Destination Lens Account"
                placeholder="Select a lens"
                class="z-30 relative fade-in"
                show-label-space
                enable-search
                search-placeholder="Search Lenses"
                :selected-obj="dupeForm.selectedLens"
                :trigger-class="showError.lens ? 'invalid-dropdown' : ''"
                :options="otherLensAccounts"
                use-parent-width
                :option-label="(opt) => opt.name"
                @change="($event) => dupeForm.selectedLens = $event"
              /> -->
              <div v-else>
                <div class="flex items-center gap-1 mt-3">
                  <BaseText
                    type="label"
                    size="sm"
                    class="text-text-muted"
                  >
                    Destination Lens Account(s)
                  </BaseText>
                </div>
                <BaseMultiSelect
                  class="mt-1 z-50"
                  :items="otherLensAccounts"
                  :selections.sync="dupeForm.selectedLens"
                  :max-dropdown-height="180"
                  empty-label="Select lens"
                  @update:selections="dupeForm.selectedFolder = null"
                />
              </div>
              <div
                v-show="showError.lens"
                class="flex items-center gap-1"
              >
                <AltAlertIcon class="text-secondary-red-100" />
                <BaseText
                  type="body"
                  size="xs"
                  class="text-secondary-red-100"
                >
                  Required
                </BaseText>
              </div>
            </div>
            <div class="relative flex-grow space-y-1">
              <div class="flex items-center gap-1">
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted"
                >
                  Folder
                </BaseText>
                <BaseText
                  class="text-text-subdued"
                  size="xs"
                >
                  (Optional)
                </BaseText>
              </div>
              <BaseSingleDropdown
                :disabled="dupeForm.selectedLens.length !== 1"
                use-parent-width
                enable-search
                class="relative z-30"
                search-placeholder="Search Folders"
                :options="folderOptions"
                :selected-obj="dupeFolderTarget"
                @change="dupeForm.selectedFolder = $event"
              >
                <template #icon>
                  <FolderIcon class="text-icon-normal" />
                </template>
              </BaseSingleDropdown>
            </div>
            <!-- Name -->
            <div class="space-y-1">
              <BaseText
                type="label"
                size="sm"
                class="text-text-muted"
              >
                Report Name
              </BaseText>
              <input
                v-model="dupeForm.name"
                :class="{ 'invalid': showError.name }"
                placeholder="Report name"
                class="form-input fade-in min-w-0 w-full px-2 py-1.5"
              >
              <!-- invalid indicator -->
              <div
                v-show="showError.name"
                class="flex items-center gap-1"
              >
                <AltAlertIcon class="text-secondary-red-100" />
                <BaseText
                  type="body"
                  size="xs"
                  class="text-secondary-red-100"
                >
                  Required
                </BaseText>
              </div>
            </div>
            <!-- Description -->
            <div class="space-y-1">
              <div class="flex items-center gap-1">
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted"
                >
                  Description
                </BaseText>
                <BaseText
                  class="text-text-subdued"
                  size="xs"
                >
                  (Optional)
                </BaseText>
              </div>
              <input
                v-model="dupeForm.description"
                placeholder="Type a description of this report"
                class="form-input fade-in min-w-0 w-full px-2 py-1.5"
              >
            </div>
          </div>
          <!-- Modal footer -->
          <div class="w-full flex justify-between items-center p-5">
            <BaseButton
              class="px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
              @click="$emit('close')"
            >
              Cancel
            </BaseButton>
            <button
              class="black-btn-primary submit-btn py-1.5 px-3 rounded-md flex items-center justify-center text-white"
              style="width: 88px"
              :disabled="loading.submit"
              @click="duplicateReport"
            >
              <BaseLoadingSpinnerCircle v-if="loading.submit" />
              <BaseText
                v-else
                type="label"
                size="sm"
              >
                Duplicate
              </BaseText>
            </button>
          </div>
        </div>
        <!-- MODAL SECTION - Duplicate report success -->
        <div
          v-else
          key="successReport"
        >
          <div class="px-5 pb-5 pt-2 border-b">
            <BaseText
              size="sm"
              class="text-neutral-500"
            >
              Your report has been successfully duplicated and is ready for use.
            </basetext>
          </div>
          <!-- Modal footer -->
          <div class="w-full flex justify-between items-center p-5">
            <BaseButton
              class="px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
              @click="$emit('close')"
            >
              Close
            </BaseButton>
            <button
              class="black-btn-primary py-1.5 px-3 rounded-md flex items-center justify-center text-white"
              @click="navToReport"
            >
              <BaseLoadingSpinnerCircle v-if="loading.submit" />
              <BaseText
                v-else
                type="label"
                size="sm"
              >
                Open Duplicated Report
              </BaseText>
            </button>
          </div>
        </div>
      </BaseHeightTransition>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LensAPI from '@/api/lens'
// Icons
import StaticCopyIcon from '../../globals/Icons/StaticCopyIcon.vue'
import AltAlertIcon from '../../globals/Icons/AltAlertIcon.vue'
import SuccessPartyIcon from '../../globals/Icons/SuccessPartyIcon.vue'
import TagRemoveIcon from '../../globals/Icons/TagRemoveIcon.vue'
import FolderIcon from '../../globals/Icons/SpyderV2Icons/FolderIcon.vue'
export default {
  name: 'DuplicateReportModal',
  components: {
    StaticCopyIcon,
    AltAlertIcon,
    SuccessPartyIcon,
    TagRemoveIcon,
    FolderIcon
  },
  props: {
    report: {
      type: Object,
      default: () => {}
    },
    shouldFetch: {
      type: Boolean,
      default: false
    },
    initLensSelection: {
      type: Object,
      default: () => {}
    },
    reportType: {
      type: [String, null],
      default: null
    }
  },
  data () {
    return {
      // Modal state
      createdNewReport: false,

      // options
      otherLensAccounts: [],

      // Form
      submitAttempted: false,
      dupeForm: {
        selectedLens: [],
        name: '',
        description: '',
        selectedFolder: null
      },
      loading: {
        lensAccounts: true,
        submit: false
      }
    }
  },
  computed: {
    ...mapGetters('LensModule', ['getLensFolders', 'getSidebarLenses']),
    dupeFolderTarget () {
      const defaultTargetFolder = this.getLensFolders?.[this.dupeForm.selectedLens?.[0]]?.find(folder => folder.name === 'Default')
      return this.dupeForm.selectedLens.length === 1 ? this.dupeForm.selectedFolder : defaultTargetFolder || null
    },
    folderOptions () {
      const selectedLensId = this.dupeForm.selectedLens[0]
      if (!selectedLensId || this.dupeForm.selectedLens.length !== 1) return []
      const folders = [...(this.getLensFolders?.[selectedLensId] || [])]
      if (folders.length) folders[0] = { ...folders[0], name: 'Custom Reports' }
      return folders
    },
    reportId () {
      return this.report?.id ?? this.$route.params?.reportId
    },
    showError () {
      return {
        lens: (!this.dupeForm.selectedLens || this.dupeForm.selectedLens.length === 0) && this.submitAttempted,
        name: !this.dupeForm.name && this.submitAttempted
      }
    },
    isFormValid () {
      return this.dupeForm.name && this.dupeForm.selectedLens && this.dupeForm.selectedLens.length > 0
    }
  },
  async mounted () {
    this.loading.lensAccounts = true

    // Init form with copied data
    if (this.report.name) { this.dupeForm.name = `${this.report.name} (Copy)` }
    // if (this.initLensSelection) { this.dupeForm.selectedLens = [this.initLensSelection] }

    try {
      const lensAccounts = this.getSidebarLenses?.length > 0 ? this.getSidebarLenses : (await LensAPI.Lens.getAllSidebar())?.data
      // TODO @Sam: verify if needed
      // const currentLensId = this.report?.lens_id
      // this.otherLensAccounts = lensAccounts.filter((lens) => lens.id !== currentLensId)
      this.otherLensAccounts = lensAccounts?.map((lens) => { return { id: lens.id, name: lens.name } })
    } catch (error) {
      // TODO @Sam: update later
      this.$showAlert({
        type: 'error',
        message: 'Could not fetch lens account, please try refreshing'
      })
      console.error('An error occurred while fetching lens accounts', error)
    } finally {
      this.loading.lensAccounts = false
    }
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    async duplicateReport () {
      this.submitAttempted = true
      if (!this.isFormValid) return
      this.loading.submit = true
      const isSoloDupe = this.dupeForm.selectedLens.length === 1
      try {
        if (!this.reportId) throw new Error('Report ID is required')
        const selectedDupeFolderId = this.dupeForm.selectedLens.length === 1 ? this.dupeFolderTarget?.id : null
        const lensFolderDestinations = this.dupeForm.selectedLens.map(lensId => ({ lens_id: lensId, folder_id: selectedDupeFolderId || this.getLensFolders?.[lensId]?.find(folder => folder.name === 'Default')?.id }))
        const dupeReports = await LensAPI.LensReport.duplicate(this.reportId, { lensDestinations: lensFolderDestinations, reportName: this.dupeForm.name, description: this.dupeForm.description })
        if (isSoloDupe) {
          const soleDupeReport = dupeReports.data[0]
          this.newReportNav = { lensId: soleDupeReport.lens_id, reportId: soleDupeReport.id }
          this.createdNewReport = true
        }
        console.log('%c Report successfully duplicated', 'color: orange')
        await this.fetchSidebarLenses()
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Error duplicating report'
        })
      } finally {
        this.loading.submit = false
        if (!isSoloDupe) {
          this.$showAlert({ type: 'success', message: 'Report successfully duplicated' })
          this.$emit('close')
        }
      }
    },
    navToReport () {
      const { lensId, reportId } = this.newReportNav
      // const reportUrl = `${process.env.VUE_APP_BASE_URL}/lens/${lensId}/${reportId}`
      this.$router.push({ name: 'LensReport', params: { lensId, reportId } })
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.submit-btn:disabled{
  box-shadow:none;
  background-color:#F6F8FA;
  color: #A4acb9;
  cursor: not-allowed
}
.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #5E6678;
  transition: color 150ms ease-in-out;
  opacity: 0.9;
}
.form-input:hover::placeholder {
  color: #303546;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

.invalid-dropdown{
  box-shadow: none;
  border: 1px solid #FF002F;
}
.form-input.invalid {
  box-shadow: none;
  border: 1px solid #FF002F;
}

/* Section transitions */
.expand-enter-active,
.expand-leave-active {
  transition-property: opacity, height;
  transition-duration: 200ms;
}

.expand-enter,
.expand-leave-to {
  opacity: 0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
